// src/web/debugPanel.js
//
// A floating developer panel for live-tuning the goggles placement, tracking
// responsiveness, and reflection brightness — with a one-click diagnostic
// report. Toggle with the gear button (top-right) or the `?panel` URL flag.
//
// Nothing in here ships to production; it's a development aid that reads/writes
// the same values the renderer and stabilizer use at runtime.

export class DebugPanel {
  /**
   * @param {object} hooks
   *  - getState(): returns a snapshot object for the report
   *  - onPlacement(axis, value): x/y/z offset (cm) or rx/ry/rz (deg) or scale
   *  - onResponsiveness(value01): 0..1 slider → maps to filter aggressiveness
   *  - onLensIntensity(value), onFrameIntensity(value)
   *  - onToggleOcclusion(bool), onToggleFilter(bool)
   *  - onReport(): returns a multi-line diagnostic string
   */
  constructor(hooks, initial) {
    this.hooks = hooks;
    this.el = null;
    this._build(initial);
  }

  _row(label, controlEl) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;margin:6px 0;';
    const l = document.createElement('label');
    l.textContent = label;
    l.style.cssText = 'flex:0 0 92px;font-size:11px;color:#aab;';
    row.appendChild(l);
    row.appendChild(controlEl);
    return row;
  }

  _slider({ min, max, step, value, onInput, fmt = (v) => v.toFixed(2) }) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'flex:1;display:flex;align-items:center;gap:8px;';
    const input = document.createElement('input');
    input.type = 'range';
    input.min = min; input.max = max; input.step = step; input.value = value;
    input.style.cssText = 'flex:1;accent-color:#00A8B5;';
    const out = document.createElement('span');
    out.textContent = fmt(value);
    out.style.cssText = 'flex:0 0 46px;text-align:right;font-family:ui-monospace,monospace;font-size:11px;color:#cde;';
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      out.textContent = fmt(v);
      onInput(v);
    });
    wrap.appendChild(input);
    wrap.appendChild(out);
    this._sliderRefs = this._sliderRefs || {};
    return { wrap, input, out };
  }

  _build(init) {
    const panel = document.createElement('div');
    panel.id = 'debug-panel';
    panel.style.cssText = `
      position:fixed; top:56px; right:12px; z-index:50;
      width:300px; max-height:84vh; overflow-y:auto;
      background:rgba(12,18,30,0.94); backdrop-filter:blur(8px);
      border:1px solid rgba(255,255,255,0.12); border-radius:14px;
      padding:14px; color:#fff; font-family:-apple-system,sans-serif;
      box-shadow:0 12px 40px rgba(0,0,0,0.5);
    `;

    const title = document.createElement('div');
    title.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;';
    title.innerHTML = `<strong style="font-size:13px;letter-spacing:0.3px;">PointAI · Alignment Debug</strong>`;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = 'background:none;border:none;color:#9ab;font-size:20px;cursor:pointer;line-height:1;';
    closeBtn.onclick = () => this.toggle(false);
    title.appendChild(closeBtn);
    panel.appendChild(title);

    // ─── Fit ───────────────────────────────────────────────────
    panel.appendChild(this._section('Fit'));

    // Placement is matrix-driven (zero per-face tuning). The one meaningful
    // fit control is how far the frame sits forward onto the nose bridge.
    const sNose = this._slider({ min:-1.0, max:3.0, step:0.05, value:init.noseDistance ?? 0.05,
      onInput:(v)=>this.hooks.onNoseDistance(v), fmt:(v)=>v.toFixed(2)+'cm' });
    panel.appendChild(this._row('Nose dist', sNose.wrap));

    const sScale = this._slider({ min:0.6, max:1.6, step:0.01, value:init.scaleValue ?? 1.0,
      onInput:(v)=>this.hooks.onScale(v), fmt:(v)=>v.toFixed(2)+'×' });
    panel.appendChild(this._row('Scale', sScale.wrap));

    const sTilt = this._slider({ min:-5, max:20, step:0.5, value:init.pantoTilt ?? 8,
      onInput:(v)=>this.hooks.onPantoTilt(v), fmt:(v)=>v.toFixed(1)+'°' });
    panel.appendChild(this._row('Panto tilt', sTilt.wrap));

    // ─── Tracking ─────────────────────────────────────────────
    panel.appendChild(this._section('Tracking'));

    const sResp = this._slider({ min:0, max:1, step:0.01, value:init.responsiveness,
      onInput:(v)=>this.hooks.onResponsiveness(v), fmt:(v)=>Math.round(v*100)+'%' });
    panel.appendChild(this._row('Speed', sResp.wrap));

    panel.appendChild(this._row('Filter', this._toggle(init.filterOn, (on)=>this.hooks.onToggleFilter(on))));
    panel.appendChild(this._row('Occlusion', this._toggle(init.occlusionOn, (on)=>this.hooks.onToggleOcclusion(on))));
    panel.appendChild(this._row('Show occluder', this._toggle(false, (on)=>this.hooks.onToggleOccluderVisible(on))));
    panel.appendChild(this._row('Head-top cover', this._toggle(init.headTopOn ?? true, (on)=>this.hooks.onToggleHeadTop(on))));
    panel.appendChild(this._row('Contact shadow', this._toggle(init.shadowOn ?? true, (on)=>this.hooks.onToggleShadow(on))));

    const sShadow = this._slider({ min:0, max:1, step:0.05, value:init.shadowOpacity ?? 0.5,
      onInput:(v)=>this.hooks.onShadowOpacity(v), fmt:(v)=>v.toFixed(2) });
    panel.appendChild(this._row('Shadow strength', sShadow.wrap));

    const sOccBias = this._slider({ min:-2.0, max:2.0, step:0.1, value:init.occBias ?? 0.0,
      onInput:(v)=>this.hooks.onOccluderBias(v), fmt:(v)=>(v>0?'+':'')+v.toFixed(1)+'cm' });
    panel.appendChild(this._row('Occ depth', sOccBias.wrap));

    // ─── Reflection ───────────────────────────────────────────
    panel.appendChild(this._section('Reflection'));

    const sLens = this._slider({ min:0, max:4, step:0.05, value:init.lensIntensity,
      onInput:(v)=>this.hooks.onLensIntensity(v), fmt:(v)=>v.toFixed(2) });
    panel.appendChild(this._row('Lens', sLens.wrap));

    const sFrame = this._slider({ min:0, max:4, step:0.05, value:init.frameIntensity,
      onInput:(v)=>this.hooks.onFrameIntensity(v), fmt:(v)=>v.toFixed(2) });
    panel.appendChild(this._row('Frame', sFrame.wrap));

    // ─── Report ───────────────────────────────────────────────
    panel.appendChild(this._section('Diagnostics'));

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:8px;margin-top:6px;';

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = 'Confirm & Generate Report';
    confirmBtn.style.cssText = `
      flex:1;padding:10px;background:#00A8B5;color:#04222a;border:none;
      border-radius:8px;font-weight:700;font-size:12px;cursor:pointer;`;
    confirmBtn.onclick = () => this._showReport();
    btnRow.appendChild(confirmBtn);

    const logBtn = document.createElement('button');
    logBtn.textContent = '📍 Generate Log';
    logBtn.style.cssText = `
      flex:1;padding:10px;background:#ff00aa;color:#fff;border:none;
      border-radius:8px;font-weight:700;font-size:12px;cursor:pointer;`;
    logBtn.onclick = () => this.hooks.onGenerateLog?.();
    btnRow.appendChild(logBtn);
    panel.appendChild(btnRow);

    const logHint = document.createElement('div');
    logHint.style.cssText = 'font-size:10px;color:#f9c;margin-top:6px;line-height:1.4;';
    logHint.textContent = 'Hold your head at an angle (still) and tap Generate Log to download a snapshot. Do it at a few angles and send the files to Claude.';
    panel.appendChild(logHint);

    const copyHint = document.createElement('div');
    copyHint.style.cssText = 'font-size:10px;color:#789;margin-top:6px;line-height:1.4;';
    copyHint.textContent = 'Generates a full diagnostic dump (placement, measurements, timing). Copy it and paste back to Claude.';
    panel.appendChild(copyHint);

    // Report output area
    this._reportArea = document.createElement('textarea');
    this._reportArea.readOnly = true;
    this._reportArea.style.cssText = `
      width:100%;height:200px;margin-top:8px;display:none;
      background:#06101c;color:#9fe;border:1px solid #1a3a4a;border-radius:8px;
      font-family:ui-monospace,monospace;font-size:10px;padding:8px;resize:vertical;`;
    panel.appendChild(this._reportArea);

    const copyBtn = document.createElement('button');
    copyBtn.textContent = 'Copy to clipboard';
    copyBtn.style.cssText = `
      width:100%;margin-top:6px;padding:8px;display:none;background:#16324a;
      color:#cde;border:1px solid #1a3a4a;border-radius:8px;font-size:11px;cursor:pointer;`;
    copyBtn.onclick = () => {
      this._reportArea.select();
      document.execCommand('copy');
      copyBtn.textContent = 'Copied ✓';
      setTimeout(() => copyBtn.textContent = 'Copy to clipboard', 1500);
    };
    this._copyBtn = copyBtn;
    panel.appendChild(copyBtn);

    document.body.appendChild(panel);
    this.el = panel;

    // Gear toggle button
    const gear = document.createElement('button');
    gear.id = 'debug-gear';
    gear.textContent = '⚙';
    gear.title = 'Alignment debug';
    gear.style.cssText = `
      position:fixed; top:14px; right:14px; z-index:51;
      width:38px;height:38px;border-radius:50%;border:none;cursor:pointer;
      background:rgba(0,168,181,0.9);color:#04222a;font-size:18px;`;
    gear.onclick = () => this.toggle();
    document.body.appendChild(gear);

    this._refs = { sNose, sScale, sTilt, sResp, sOccBias, sLens, sFrame };
  }

  _section(text) {
    const s = document.createElement('div');
    s.textContent = text;
    s.style.cssText = `
      font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#00A8B5;
      margin:14px 0 4px;padding-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.08);`;
    return s;
  }

  _toggle(on, onChange) {
    const btn = document.createElement('button');
    const paint = () => {
      btn.textContent = on ? 'ON' : 'OFF';
      btn.style.background = on ? '#19d27d' : '#444';
      btn.style.color = on ? '#04220f' : '#ccc';
    };
    btn.style.cssText = 'flex:1;padding:6px;border:none;border-radius:6px;font-weight:700;font-size:11px;cursor:pointer;';
    paint();
    btn.onclick = () => { on = !on; paint(); onChange(on); };
    return btn;
  }

  _showReport() {
    const report = this.hooks.onReport();
    this._reportArea.value = report;
    this._reportArea.style.display = 'block';
    this._copyBtn.style.display = 'block';
    console.log('═══════ POINTAI DIAGNOSTIC REPORT ═══════\n' + report);
  }

  setNoseDistance(cm) {
    const r = this._refs && this._refs.sNose;
    if (r) { r.input.value = cm; r.out.textContent = cm.toFixed(2) + 'cm'; }
  }

  setScale(v) {
    const r = this._refs && this._refs.sScale;
    if (r) { r.input.value = v; r.out.textContent = v.toFixed(2) + '×'; }
  }

  setPantoTilt(v) {
    const r = this._refs && this._refs.sTilt;
    if (r) { r.input.value = v; r.out.textContent = v.toFixed(1) + '°'; }
  }

  setResponsiveness(v) {
    const r = this._refs && this._refs.sResp;
    if (r) { r.input.value = v; r.out.textContent = v.toFixed(2); }
  }

  setOccluderBias(cm) {
    const r = this._refs && this._refs.sOccBias;
    if (r) { r.input.value = cm; r.out.textContent = (cm > 0 ? '+' : '') + cm.toFixed(1) + 'cm'; }
  }

  toggle(force) {
    const show = force === undefined ? this.el.style.display === 'none' : force;
    this.el.style.display = show ? 'block' : 'none';
  }
}
